import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'ferritin', 'sex', 'activity',
    'ferritinDisplay', 'statusContainer', 'statusDot', 'statusName',
    'statusDescription', 'labRange', 'optimalRange', 'recommendation'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const ferritin = parseFloat(this.ferritinTarget.value) || 0
    const sex = this.sexTargets.find((r) => r.checked)?.value || 'male'
    const activity = this.activityTarget.value

    if (ferritin <= 0) {
      return
    }

    this.ferritinDisplayTarget.textContent = ferritin

    // Set ranges based on sex
    const ranges = this.getRanges(sex)
    this.labRangeTarget.textContent = ranges.lab
    this.optimalRangeTarget.textContent = ranges.optimal

    // Get interpretation
    const result = this.interpret(ferritin, sex, activity)
    this.updateDisplay(result)
  }

  getRanges(sex) {
    if (sex === 'female') {
      return {
        lab: '12-150 ng/mL',
        optimal: '40-100 ng/mL'
      }
    }
    return {
      lab: '12-300 ng/mL',
      optimal: '50-150 ng/mL'
    }
  }

  interpret(ferritin, sex, activity) {
    const isAthlete = activity === 'athlete'
    const thresholds = sex === 'female' ?
      { deficient: 20, low: 40, optimalLow: 40, optimalHigh: 100, high: 200 } :
      { deficient: 30, low: 50, optimalLow: 50, optimalHigh: 150, high: 300 }

    // Athletes have higher optimal ranges
    if (isAthlete) {
      thresholds.optimalLow = sex === 'female' ? 70 : 100
    }

    if (ferritin < thresholds.deficient) {
      return {
        name: 'DEFICIENT',
        color: 'red-500',
        borderColor: 'border-red-500',
        bgColor: 'bg-red-50',
        description: 'Your ferritin is low, indicating depleted iron stores. ' +
          'This can cause fatigue, hair loss, and impaired performance.',
        recommendation: 'Discuss iron supplementation with your healthcare provider. ' +
          'Rule out causes of iron loss (bleeding, malabsorption). ' +
          'Pair iron with vitamin C and avoid taking with calcium.'
      }
    }
    if (ferritin < thresholds.low) {
      return {
        name: 'LOW',
        color: 'yellow-500',
        borderColor: 'border-yellow-500',
        bgColor: 'bg-yellow-50',
        description: 'Your ferritin is below optimal. ' +
          'You may experience subtle symptoms like fatigue or reduced exercise tolerance.',
        recommendation: 'Consider increasing iron-rich foods (red meat, shellfish, legumes). ' +
          'Supplementation may help if dietary changes insufficient. Retest in 3 months.'
      }
    }
    if (ferritin >= thresholds.optimalLow && ferritin <= thresholds.optimalHigh) {
      return {
        name: 'OPTIMAL',
        color: 'green-500',
        borderColor: 'border-green-500',
        bgColor: 'bg-green-50',
        description: 'Your ferritin is in the optimal range. ' +
          'Iron stores are adequate for health and performance.',
        recommendation: 'Maintain current iron intake. ' +
          'Continue to include iron-rich foods in your diet. ' +
          'No supplementation needed unless directed.'
      }
    }
    if (ferritin > thresholds.high) {
      return {
        name: 'ELEVATED',
        color: 'red-500',
        borderColor: 'border-red-500',
        bgColor: 'bg-red-50',
        description: 'Your ferritin is elevated. ' +
          'This could indicate inflammation, liver issues, or iron overload conditions.',
        recommendation: 'Discuss with your healthcare provider. ' +
          'Additional testing (CRP, iron panel, transferrin saturation) ' +
          'may be needed to determine the cause.'
      }
    }
    // Between low and optimal, or between optimal and high
    return {
      name: 'ADEQUATE',
      color: 'green-400',
      borderColor: 'border-green-400',
      bgColor: 'bg-green-50',
      description: 'Your ferritin is within the reference range ' +
        'but may be suboptimal depending on your activity level.',
      recommendation: 'Monitor and ensure adequate iron intake. ' +
        'Athletes and menstruating individuals may benefit from levels ' +
        'at the higher end of optimal.'
    }
  }

  updateDisplay(result) {
    this.statusDotTarget.className = `w-4 h-4 bg-${result.color}`
    this.statusNameTarget.textContent = result.name
    this.statusDescriptionTarget.textContent = result.description
    this.recommendationTarget.textContent = result.recommendation
    this.statusContainerTarget.className = `p-6 ${result.borderColor} border-2 ${result.bgColor}`
  }
}
